// ── SHARED PROMPT RULES ───────────────────────────────────────────
const CSS_VARS =
  '--primary, --primary-dark, --primary-light, --accent, --secondary, --bg, --bg-alt, --surface, --surface-hover, --text, --text-muted, --border, --grad-from, --grad-to, --font-h, --font-b, --radius, --radius-lg, --shadow, --shadow-lg, --ease, --duration-fast, --duration-medium, --duration-slow';

const MINIFY_RULE =
  'Output minification: CSS fully minified inside <style> blocks (no newlines, single-line rules). HTML body: no blank lines, no indentation, no whitespace between tags. This saves tokens and reduces page weight. Include @media(prefers-reduced-motion:reduce){.reveal{opacity:1!important;transform:none!important}} in every section style block.';

const VIDEO_RULE = `Video embeds (YouTube/Vimeo): use .video-facade pattern — div with data-video-id, data-provider, background-image thumbnail. Shell provides CSS + click-to-play script. No inline video scripts in sections.

Direct video files (.mp4, .webm): use <video> HTML tag with autoplay, muted, loop, playsinline attributes for background video. NEVER use background-image:url() with a video file — CSS cannot render video.

Loading placeholder: when a poster/thumbnail URL is available, ALWAYS set the poster attribute on the <video> tag AND set the same image as the container's background-image. This shows the thumbnail instantly while the video loads. Pattern:
<div class="video-bg" style="background-image:url('POSTER_URL');background-size:cover;background-position:center">
  <video autoplay muted loop playsinline poster="POSTER_URL" style="position:absolute;inset:0;width:100%;height:100%;object-fit:cover">
    <source src="VIDEO_URL" type="video/mp4">
  </video>
</div>
The container background-image is visible immediately. The poster attribute prevents a black frame. The video plays over both once loaded. The container must have position:relative and overflow:hidden.`;

// SECTION_TOKENS_DEFAULT (10000) — defined in js/constants.js

// ── STRUCTURED OUTPUT SCHEMAS (Phase 1.6) ────────────────────────
// Constrained decoding guarantees valid JSON matching these schemas.
// Descriptions only where they carry quality guidance or disambiguation.
// Claude sees descriptions via structured output system prompt injection.

/** @type {object} */
const _FONT_TOKEN = {
  type: 'object',
  additionalProperties: false,
  properties: {
    family: { type: 'string', description: 'Google Fonts family name' },
    weights: { type: 'string', description: 'Weight range e.g. "400/600/800"' },
    role: { type: 'string', description: 'Usage: "heading" or "body"' },
    style: { type: 'string', description: 'Aesthetic description of the font character' },
  },
  required: ['family', 'weights', 'role', 'style'],
};

// SECTION_ITEM: enums removed to reduce grammar complexity for structured outputs.
// Valid values enforced by prompt text.
/** @type {object} */
const _SECTION_ITEM = {
  type: 'object',
  additionalProperties: false,
  properties: {
    id: {
      type: 'string',
      description:
        'Use type name as ID (e.g. "hero", "faq"). Append number for duplicates: "cta-2"',
    },
    type: {
      type: 'string',
      description:
        'Section type: header, hero, features, services, social-proof, trust-ticker, testimonials, faq, stats, about, process, contact, subscribe, service-area, cta, footer, content, problem, solution, pricing, video',
    },
    title: { type: 'string', description: 'Human-readable section title for the admin panel' },
    purpose: {
      type: 'string',
      description: 'Conversion rationale — why this section exists in the persuasion flow',
    },
    copyNotes: {
      type: 'string',
      description:
        "Specific content direction with real facts, names, numbers from the brief. The section builder's only source of business content — vague notes produce generic sections.",
    },
    layout: {
      type: 'string',
      enum: [
        'centered',
        'split-left-image',
        'split-right-image',
        '3-col-grid',
        'hero-split',
        'horizontal-bar',
        'alternating-rows',
        'bento-grid',
        'pills-grid',
        'two-column-form',
        'centered-form',
        'horizontal-cards',
        'owner-grid',
        'logo-left-nav-right',
      ],
    },
    bgStyle: {
      type: 'string',
      enum: ['light', 'dark', 'gradient', 'primary', 'image', 'transparent-to-solid'],
    },
    visualBrief: {
      type: 'string',
      description:
        'Art director brief: describe exactly how this section should LOOK — spatial composition, background treatment, image placement, decorative elements, animation intent. The section builder executes this brief literally.',
    },
  },
  required: ['id', 'type', 'title', 'purpose', 'copyNotes', 'layout', 'bgStyle', 'visualBrief'],
};

// Keyed token items for typed arrays — grammar compiles item schema once
// (unlike keyed objects which compile each property position separately)
/** @type {object} */
const _COLOR_ITEM = {
  type: 'object',
  additionalProperties: false,
  properties: {
    key: {
      type: 'string',
      description:
        'CSS variable name: primary, primary-dark, primary-light, accent, secondary, bg, bg-alt, surface, surface-hover, text, text-muted, border, gradient-from, gradient-to',
    },
    value: { type: 'string', description: 'Hex color value' },
    name: {
      type: 'string',
      description: 'Evocative name (e.g. "Midnight Ink", "Solar Flare"), not just "Blue"',
    },
    role: {
      type: 'string',
      description: 'Usage instruction (e.g. "CTA buttons and interactive highlights")',
    },
  },
  required: ['key', 'value', 'name', 'role'],
};

/** @type {object} */
const _SPACING_ITEM = {
  type: 'object',
  additionalProperties: false,
  properties: {
    key: {
      type: 'string',
      description:
        'Token name: section-padding, container-width, container-padding, card-padding, gap, gap-lg',
    },
    value: { type: 'string', description: 'CSS value' },
  },
  required: ['key', 'value'],
};

/** @type {object} */
const _EFFECT_ITEM = {
  type: 'object',
  additionalProperties: false,
  properties: {
    key: { type: 'string', description: 'Token name: radius, radius-lg, shadow, shadow-lg, glass' },
    value: { type: 'string', description: 'CSS value' },
    role: { type: 'string', description: 'Where/how this effect is applied' },
  },
  required: ['key', 'value', 'role'],
};

/** @type {object} */
const _DESIGN_VISION = {
  type: 'object',
  additionalProperties: false,
  properties: {
    concept: {
      type: 'string',
      description:
        '2-3 sentences: the overall visual concept for this specific business. What makes this design unique and impossible to confuse with a template? Reference the business identity — a manufacturing company might be "industrial cinema," a bakery might be "warm editorial."',
    },
    pageFlow: {
      type: 'string',
      description:
        'How the visual story unfolds as the user scrolls. Start with what the visitor sees BEFORE scrolling (hero + visible edge of next section — this is the 2-second first impression). Then describe the emotional arc: dramatic opening → informational middle → credibility build → conversion close.',
    },
    signatureElement: {
      type: 'string',
      description:
        'One distinctive visual decision that makes this design memorable — an unexpected layout choice, a unique hero treatment, a bold decorative element. This is what prevents generic output.',
    },
  },
  required: ['concept', 'pageFlow', 'signatureElement'],
};

/** @type {object} */
const _DESIGN_TOKENS_SCHEMA = {
  type: 'object',
  additionalProperties: false,
  properties: {
    // Typed arrays — grammar compiles the item schema once, unlike keyed objects
    // which compile each fixed-position property separately (causing grammar limits).
    colors: {
      type: 'array',
      description:
        '14 color tokens. Accent = CTA button color. Text must have 4.5:1 contrast vs bg. Border = ghost borders at 15% opacity.',
      items: _COLOR_ITEM,
    },
    typography: {
      type: 'object',
      additionalProperties: false,
      properties: {
        heading: _FONT_TOKEN,
        body: _FONT_TOKEN,
        scale: {
          type: 'object',
          additionalProperties: false,
          description: 'Modular type scale using clamp() values',
          properties: {
            display: { type: 'string' },
            h1: { type: 'string' },
            h2: { type: 'string' },
            h3: { type: 'string' },
            body: { type: 'string' },
            small: { type: 'string' },
          },
          required: ['display', 'h1', 'h2', 'h3', 'body', 'small'],
        },
      },
      required: ['heading', 'body', 'scale'],
    },
    spacing: {
      type: 'array',
      description: '6 spacing tokens',
      items: _SPACING_ITEM,
    },
    effects: {
      type: 'array',
      description: '5 effect tokens. Glass = "none" for warm/organic aesthetics.',
      items: _EFFECT_ITEM,
    },
    animation: {
      type: 'object',
      additionalProperties: false,
      properties: {
        duration: {
          type: 'object',
          additionalProperties: false,
          properties: {
            fast: { type: 'string' },
            medium: { type: 'string' },
            slow: { type: 'string' },
          },
          required: ['fast', 'medium', 'slow'],
        },
        easing: { type: 'string' },
        stagger: { type: 'string' },
        style: { type: 'string', enum: ['smooth', 'spring', 'snappy'] },
      },
      required: ['duration', 'easing', 'stagger', 'style'],
    },
  },
  required: ['colors', 'typography', 'spacing', 'effects', 'animation'],
};

/** @type {object} */
const CONCEPT_SCHEMA = {
  type: 'object',
  additionalProperties: false,
  properties: {
    siteName: { type: 'string', description: '2-3 words max' },
    heroHeadline: {
      type: 'string',
      description: '6-10 words, specific value proposition, benefit-driven',
    },
    heroSubheadline: {
      type: 'string',
      description: '15-20 words, qualifies audience and adds proof',
    },
    ctaText: {
      type: 'string',
      description: '3-5 words, starts with verb (e.g. "Book Free Inspection")',
    },
    secondaryCta: { type: 'string' },
    brandVoiceSample: {
      type: 'string',
      description: '2-3 sentences demonstrating exact brand tone — style guide for all sections',
    },
    designVision: _DESIGN_VISION,
    designTokens: _DESIGN_TOKENS_SCHEMA,
    sections: {
      type: 'array',
      items: _SECTION_ITEM,
      description:
        '7-10 content sections between header (first) and footer (last). Follow industry conversion flow.',
    },
    strategy: {
      type: 'string',
      description: 'CRO strategy: persuasion flow from awareness to action',
    },
  },
  required: [
    'siteName',
    'heroHeadline',
    'heroSubheadline',
    'ctaText',
    'secondaryCta',
    'brandVoiceSample',
    'designVision',
    'designTokens',
    'sections',
    'strategy',
  ],
};

/** @type {object} */
const IMAGE_QUERIES_SCHEMA = {
  type: 'object',
  additionalProperties: false,
  properties: {
    queries: {
      type: 'array',
      items: { type: 'string' },
      description:
        '3-5 Unsplash queries (2-4 words), driven by the design vision and section visual briefs',
    },
  },
  required: ['queries'],
};

/** @type {object} */
const VISION_REFINE_SCHEMA = {
  type: 'object',
  additionalProperties: false,
  properties: {
    designVision: _DESIGN_VISION,
    designTokens: _DESIGN_TOKENS_SCHEMA,
    sectionUpdates: {
      type: 'array',
      items: {
        type: 'object',
        additionalProperties: false,
        properties: {
          id: { type: 'string', description: 'Section ID to update' },
          visualBrief: { type: 'string', description: 'Updated visual brief for this section' },
        },
        required: ['id', 'visualBrief'],
      },
      description: 'Only sections whose visualBrief is affected — empty array if none change',
    },
    summary: { type: 'string', description: 'One-line summary of what changed' },
  },
  required: ['designVision', 'designTokens', 'sectionUpdates', 'summary'],
};

/** @type {object} */
const KEEP_TOKENS_SCHEMA = {
  type: 'object',
  properties: {
    designTokens: _DESIGN_TOKENS_SCHEMA,
    summary: { type: 'string', description: 'One-line summary of what changed' },
  },
  required: ['designTokens', 'summary'],
  additionalProperties: false,
};

// FACTS: Used by MARKETING_BRIEF_SCHEMA (site-analysis, doc-analysis, qa-enrichment).
// Grammar-safe: sub-objects flattened to pipe-delimited strings to avoid 4 extra object schemas.
// After parsing, call hydrateBrief() to convert back to structured objects for UI consumption.
/** @type {object} */
const _FACTS_SCHEMA = {
  type: 'object',
  properties: {
    phones: { type: 'array', items: { type: 'string' }, description: 'Exact format as on site' },
    emails: { type: 'array', items: { type: 'string' } },
    addresses: { type: 'array', items: { type: 'string' } },
    serviceArea: { type: 'string' },
    services: {
      type: 'array',
      items: { type: 'string' },
      description: 'Each: "name | description | price" — use empty string for missing fields',
    },
    credentials: {
      type: 'array',
      items: { type: 'string' },
      description: 'Exact text (e.g. "BBB A+ Rating")',
    },
    namedStaff: { type: 'array', items: { type: 'string' }, description: 'Each: "name | role"' },
    guarantees: { type: 'array', items: { type: 'string' } },
    yearsInBusiness: { type: 'string', description: 'Empty string if not found' },
    testimonials: {
      type: 'array',
      items: { type: 'string' },
      description: 'Each: "quote | author" — preserve original wording exactly',
    },
    stats: {
      type: 'array',
      items: { type: 'string' },
      description: 'Each: "number | label" — extract all numeric claims',
    },
  },
  required: [
    'phones',
    'emails',
    'addresses',
    'serviceArea',
    'services',
    'credentials',
    'namedStaff',
    'guarantees',
    'yearsInBusiness',
    'testimonials',
    'stats',
  ],
  additionalProperties: false,
};

/** @type {object} */
const _BRIEF_FONT = {
  type: 'object',
  additionalProperties: false,
  properties: {
    family: { type: 'string', description: 'Map to closest Google Font when possible' },
    style: { type: 'string', description: 'Aesthetic description of the font character' },
  },
  required: ['family', 'style'],
};

// _BRIEF_IMAGE removed — images come from site-structure per-section instead

/** @type {object} */
const _BRIEF_COLOR = {
  type: 'object',
  additionalProperties: false,
  properties: {
    hex: { type: 'string' },
    name: { type: 'string', description: 'Evocative name, not just "Blue"' },
    role: {
      type: 'string',
      description:
        'One of: primary, accent, secondary, bg, bg-alt, surface, text, text-muted, border, gradient',
    },
  },
  required: ['hex', 'name', 'role'],
};

/** @type {object} */
const MARKETING_BRIEF_SCHEMA = {
  type: 'object',
  properties: {
    companyName: { type: 'string' },
    tagline: { type: 'string', description: 'Primary tagline or hero headline' },
    businessOverview: {
      type: 'string',
      description: '2-3 sentences: what they do, who they serve, market position',
    },
    industry: { type: 'string' },
    colors: { type: 'array', description: '5-8 colors minimum', items: _BRIEF_COLOR },
    fonts: {
      type: 'object',
      additionalProperties: false,
      properties: { heading: _BRIEF_FONT, body: _BRIEF_FONT },
      required: ['heading', 'body'],
    },
    visualAesthetic: {
      type: 'array',
      items: { type: 'string' },
      description: '3-6 design language tags',
    },
    toneOfVoice: {
      type: 'array',
      items: { type: 'string' },
      description: '3-5 writing style tags',
    },
    brandValues: {
      type: 'array',
      items: { type: 'string' },
      description: '3-5 core values inferred from messaging',
    },
    logoUrl: { type: 'string' },
    facts: _FACTS_SCHEMA,
    targetAudience: {
      type: 'string',
      description: 'Inferred from messaging, case studies, testimonials',
    },
    customerProblem: { type: 'string' },
    differentiator: { type: 'string', description: 'Unique angle, method, or advantage' },
    ctaTexts: { type: 'array', items: { type: 'string' } },
    proofPoints: {
      type: 'array',
      items: { type: 'string' },
      description: 'Trust signals: awards, client counts, media mentions',
    },
    objections: {
      type: 'array',
      items: { type: 'string' },
      description: 'Likely buyer objections inferred from FAQ or guarantees',
    },
    socialLinks: {
      type: 'array',
      items: { type: 'string' },
      description: '"platform: url" — only platforms with actual links',
    },
    copyrightText: {
      type: 'string',
      description: 'Exact copyright line from footer — look for © or "Copyright"',
    },
    navLinks: { type: 'array', items: { type: 'string' } },
  },
  required: [
    'companyName',
    'tagline',
    'businessOverview',
    'industry',
    'colors',
    'fonts',
    'visualAesthetic',
    'toneOfVoice',
    'brandValues',
    'logoUrl',
    'facts',
    'targetAudience',
    'customerProblem',
    'differentiator',
    'ctaTexts',
    'proofPoints',
    'objections',
    'socialLinks',
    'copyrightText',
    'navLinks',
  ],
  additionalProperties: false,
};

/** @type {object} */
const SITE_STRUCTURE_SCHEMA = {
  type: 'array',
  items: {
    type: 'object',
    properties: {
      order: { type: 'integer' },
      type: {
        type: 'string',
        enum: [
          'header',
          'hero',
          'features',
          'services',
          'social-proof',
          'trust-ticker',
          'testimonials',
          'faq',
          'stats',
          'about',
          'process',
          'contact',
          'subscribe',
          'service-area',
          'cta',
          'footer',
          'content',
        ],
      },
      label: { type: 'string' },
      content: {
        type: 'string',
        description:
          'Labeled structure with verbatim text: Heading: "...", Subtext: "...", Button: "...", Quote: "...", Author: "..."',
      },
      images: {
        type: 'array',
        items: {
          type: 'object',
          additionalProperties: false,
          properties: {
            url: { type: 'string' },
            position: { type: 'string' },
            description: { type: 'string' },
          },
          required: ['url', 'position', 'description'],
        },
      },
      video: {
        type: 'object',
        additionalProperties: false,
        description: 'Video embedded in this section (YouTube, Vimeo, or hosted). Omit if no video.',
        properties: {
          provider: { type: 'string', enum: ['youtube', 'vimeo', 'hosted'] },
          url: { type: 'string', description: 'YouTube/Vimeo watch URL or hosted mp4 URL' },
          posterUrl: { type: 'string', description: 'Poster/thumbnail image URL if available' },
        },
        required: ['provider', 'url'],
      },
    },
    required: ['order', 'type', 'label', 'content', 'images'],
    additionalProperties: false,
  },
};

/** @type {object} */
const QA_ISSUES_SCHEMA = {
  type: 'array',
  items: {
    type: 'object',
    additionalProperties: false,
    properties: {
      section: { type: 'string', description: 'Section ID where the issue was found' },
      issue: { type: 'string', description: 'What is wrong and why it matters' },
      fix: { type: 'string', description: 'Specific code change needed to resolve the issue' },
    },
    required: ['section', 'issue', 'fix'],
  },
};

/** @type {object} */
const COPYWRITING_BRIEF_SCHEMA = {
  type: 'object',
  additionalProperties: false,
  properties: {
    positioning: { type: 'string', description: 'One sentence — what this business truly is' },
    audiencePrimary: { type: 'string' },
    voiceSummary: {
      type: 'string',
      description: '3 adjectives describing the correct brand voice',
    },
    currentWeaknesses: { type: 'array', items: { type: 'string' } },
    recommendedHeadline: { type: 'string' },
    recommendedCTA: { type: 'string' },
    keyMessages: { type: 'array', items: { type: 'string' } },
    sectionOrder: {
      type: 'array',
      items: { type: 'string' },
      description: 'Recommended section type order for conversion',
    },
  },
  required: [
    'positioning',
    'audiencePrimary',
    'voiceSummary',
    'currentWeaknesses',
    'recommendedHeadline',
    'recommendedCTA',
    'keyMessages',
    'sectionOrder',
  ],
};

/** @type {object} */
const GAP_ANALYSIS_SCHEMA = {
  type: 'object',
  additionalProperties: false,
  properties: {
    questions: {
      type: 'array',
      items: {
        type: 'object',
        additionalProperties: false,
        properties: {
          category: { type: 'string' },
          question: { type: 'string' },
        },
        required: ['category', 'question'],
      },
    },
  },
  required: ['questions'],
};

// ── MODULE EXPORTS (activate when switching to ES modules) ──────
export {
  _BRIEF_COLOR,
  _BRIEF_FONT,
  _COLOR_ITEM,
  _DESIGN_TOKENS_SCHEMA,
  _DESIGN_VISION,
  _EFFECT_ITEM,
  _FACTS_SCHEMA,
  _FONT_TOKEN,
  _SECTION_ITEM,
  _SPACING_ITEM,
  CONCEPT_SCHEMA,
  COPYWRITING_BRIEF_SCHEMA,
  CSS_VARS,
  GAP_ANALYSIS_SCHEMA,
  IMAGE_QUERIES_SCHEMA,
  KEEP_TOKENS_SCHEMA,
  MARKETING_BRIEF_SCHEMA,
  MINIFY_RULE,
  QA_ISSUES_SCHEMA,
  SITE_STRUCTURE_SCHEMA,
  VIDEO_RULE,
  VISION_REFINE_SCHEMA,
};
